import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Stack, Text, Button } from '@mantine/core';
import { IconArrowLeft, IconMail, IconMailOpened } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { markEmailConfirmedLocally } from '@/utils/emailConfirmationMarker';
import { PageIntro } from '@/components/rides/PageIntro';
import { Callout, LoadingState } from '@/components/ui';
import { useCopy } from '@/lib/settings';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authService = useAuthService();
  const { t } = useCopy();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [confirming, setConfirming] = useState(!!token);
  const [confirmed, setConfirmed] = useState(false);

  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState(false);

  useEffect(() => {
    if (!token || !email) return;
    let cancelled = false;
    (async () => {
      try {
        await authService.confirmEmail({ email, token });
        if (!cancelled) {
          // Local marker so the banner stops showing for this email straight away. The cached JWT
          // still carries EmailConfirmed=false until the member signs in again.
          markEmailConfirmedLocally(email);
          setConfirmed(true);
          setTimeout(() => navigate('/auth/login', { replace: true }), 2500);
        }
      } catch {
        /* shown below: the link has expired or was already used */
      } finally {
        if (!cancelled) setConfirming(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, email, authService, navigate]);

  const onResend = async () => {
    setResending(true);
    setResendError(false);
    try {
      await authService.resendConfirmation({ email, appUrl: window.location.origin });
      setResent(true);
    } catch {
      setResendError(true);
    } finally {
      setResending(false);
    }
  };

  const resendBlock = email && (
    <>
      <Button onClick={onResend} loading={resending} leftSection={<IconMail size={20} />} variant="default" fullWidth>
        Send the email again
      </Button>
      {resent && <Callout tone="ok" live>We've sent a new link to <strong>{email}</strong>.</Callout>}
      {resendError && <Callout tone="bad" live>We couldn't send the email just now. Please try again.</Callout>}
    </>
  );

  return (
    <div className="rides-main">
      <PageIntro title={confirmed ? 'Email confirmed' : token ? 'Confirm your email' : 'Check your email'} />
      <div className="rides-card" style={{ padding: 18 }}>
        {confirming ? (
          <LoadingState variant="spinner" label="Confirming your email address" />
        ) : confirmed ? (
          <Stack gap="md">
            <Callout tone="ok" live>{t('confirm.done')}</Callout>
            <Button component={Link} to="/auth/login" fullWidth>Sign in now</Button>
          </Stack>
        ) : token ? (
          <Stack gap="md">
            <Callout tone="bad" live>{t('confirm.invalid')}</Callout>
            {email && <Text>Need a new link? We can send one to <strong>{email}</strong>.</Text>}
            {resendBlock}
            <Button component={Link} to="/auth/login" variant="subtle" leftSection={<IconArrowLeft size={18} />} fullWidth>Back to sign in</Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <span className="rides-empty" style={{ padding: 0 }}><span className="ic" style={{ margin: 0 }} aria-hidden="true"><IconMailOpened size={28} /></span></span>
              <Text style={{ flex: 1 }}>{t('confirm.sent', undefined, { email: email || 'your email address' })}</Text>
            </div>
            <Text c="dimmed">{t('confirm.spam')}</Text>
            {resendBlock}
            <Button component={Link} to="/auth/login" variant="subtle" leftSection={<IconArrowLeft size={18} />} fullWidth>Back to sign in</Button>
          </Stack>
        )}
      </div>
    </div>
  );
}
